'use client'

import { InformationDialog, PaymentType } from '@repo/shared/components'
import { useTranslate } from '@repo/shared/hooks'
import type { ProjectsProject } from '@repo/shared/types'
import { globalConfig } from '@repo/shared/utils'
import {
  AspectRatio,
  Card,
  Col,
  PriceChangeBadge,
  Row,
  Typography,
} from '@repo/ui/components'
import Image from 'next/image'

export interface ProjectDetailHeaderProps {
  project: ProjectsProject
}

const ImageComp = ({ src }: { src?: string }) => {
  const url = src
    ? `${globalConfig.baseImageUrl}${src}`
    : '/images/placeholder.webp'

  return (
    <AspectRatio className=' aspect-1/1'>
      <Image
        src={url}
        alt='Photo of property'
        fill
        sizes='(max-width: 768px) 100vw, 32px'
        className='h-full w-full  rounded-2xs object-cover'
        loading='lazy'
      />
    </AspectRatio>
  )
}

export const ProjectDetailHeader = ({ project }: ProjectDetailHeaderProps) => {
  const { t } = useTranslate()
  const url = project?.logoURL
  const PriceInfo = ({ className }: { className?: string }) => (
    <Col className={`mt-xl md:pr-5xl md:mr-2xs ${className}`}>
      <Row className='justify-between py-xs'>
        <Row className='gap-2xs'>
          <Typography size='xs' color='text-neutral-surface-80'>
            {t('projectsPage_price_per_meter_hint')}
          </Typography>
          <InformationDialog title={t('projectsPage_price_per_meter_hint')}>
            {t('info_price_per_meter')}
          </InformationDialog>
        </Row>
        <PaymentType value={project?.pricePerMeter} />
      </Row>
      <Row className='justify-between py-xs'>
        <Typography size='xs' color='text-neutral-surface-80'>
          {t('common_change_24h')}
        </Typography>
        <PriceChangeBadge value={project?.percentChange} size='lg' />
      </Row>
    </Col>
  )

  return (
    <Card className='gap-lg !px-0 pb-0 sm:p-lg border-none'>
      <Row className='gap-0 md:gap-lg items-start flex-col md:flex-row'>
        <Row className='justify-between items-center gap-lg'>
          <div className='w-3xl hidden md:block'>
            <ImageComp src={url} />
          </div>

          <Col className='gap-2xs flex-1'>
            <Typography
              size='lg'
              weight='bold'
              color='text-neutral-surface-100'
            >
              {project?.name}{' '}
            </Typography>

            <Row className='gap-xs items-center w-fit'>
              <Typography size='xs' color='text-neutral-surface-60'>
                <span className='font-[AzarMehr]'>{project?.token}</span>{' '}
              </Typography>
            </Row>
          </Col>
          <div className='w-6xl block md:hidden'>
            <ImageComp src={url} />
          </div>
        </Row>
        <PriceInfo className='block md:hidden mb-md' />
      </Row>

      <PriceInfo className='hidden md:block' />
    </Card>
  )
}
